<script setup lang="ts">
import {computed, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import AppModal from '@/components/appModal.vue';
import InfoModalStoneType from './infoModalStoneType.vue';
import DiamondDetailClarityContent from "@/components/diamonds/details/DiamondDetailClarityContent.vue";
import DiamondDetailColorContent from "@/components/diamonds/details/DiamondDetailColorContent.vue";
import DiamondDetailCutContent from "@/components/diamonds/details/DiamondDetailCutContent.vue";
import DiamondDetailFluorescenceContent from "@/components/diamonds/details/DiamondDetailFluorescenceContent.vue";
import DiamondDetailCaratContent from "@/components/diamonds/details/DiamondDetailCaratContent.vue";
import InfoModalStonePresets from "@/components/info/infoModalStonePresets.vue";

const props = withDefaults(defineProps<{
  id: string;
  value?: any;
  additionalValue?: string;
  stoneType?: string;
}>(), {
  value: null,
  additionalValue: '',
  stoneType: 'earth',
});

const finalId = computed(() => {
  const idMap: Record<string, string> = {
    stone_size: 'stoneSize',
    stone_type: 'stoneType',
    stone_quality: 'stonePreset',
  };
  const id = props.id || ""

  return idMap[id] || id;
});


const isOpen = ref(false);

const {t} = useI18n();

const modalTitle = computed(() => {
  if (finalId.value === 'stoneSize') {
    const key = typeof props.value === 'object' && props.value !== null ? 'stoneSizeSelection' : 'stoneSize';
    return t(`components.infoModal.titles.${key}`);
  }
  return t(`components.infoModal.titles.${finalId.value}`, t('components.infoModal.titles.information'));
});

const supportedModals = ['stoneClarity', 'stoneColor', 'stoneCut', 'stoneFluorescence', 'stoneSize', 'stoneType', 'stonePreset'];

const isSupported = computed(() => {
  if (props.additionalValue !== '' && finalId.value == 'stone_quality') {
    return parseFloat(props.additionalValue) >= 0.3;
  }
  return supportedModals.includes(finalId.value)
});

const toggleModal = () => {
  if (isSupported.value) {
    isOpen.value = !isOpen.value;
  }
};

</script>

<template>
  <div v-if="isSupported" class="inline-block align-middle my-auto" @click.stop>
    <div
        class="cursor-pointer ml-1 flex items-center justify-center text-brand-main hover:text-gray-600 transition-colors"
        @click="toggleModal">
      <svg xmlns="http://www.w3.org/2000/svg" class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"
           stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
    </div>

    <app-modal v-model="isOpen" :label="modalTitle" :duration="300">
      <Transition name="fade">
        <div v-if="isOpen"
             class="fixed inset-0 z-1000 flex items-start justify-center p-4 sm:p-6 bg-black/20 backdrop-blur-xs"
             @click="isOpen = false">
          <Transition name="modal-pop" appear>
            <div v-if="isOpen"
                 class="bg-white rounded-[2.5rem] shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto flex flex-col relative"
                 @click.stop>

              <!-- Close button -->
              <button type="button" @click="isOpen = false"
                      class="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center text-white bg-brand-main hover:bg-gray-400 transition-colors rounded-full shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="3"
                     stroke="currentColor" class="size-6">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>

              <!-- Header -->
              <div class="pt-10 pb-8 px-8 text-left">
                <h3 class="text-xl font-serif-title text-gray-900 font-ivymode font-minerva">
                  {{ modalTitle }}
                </h3>
              </div>

              <!-- Content -->
              <div class="px-8 pb-8 overflow-y-auto text-center">
                <div class="max-w-4xl mx-auto flex flex-col items-center">
                  <DiamondDetailClarityContent v-if="finalId === 'stoneClarity'" :clarity="value" mobile-mode>
                    <template #title><span></span></template>
                  </DiamondDetailClarityContent>
                  <DiamondDetailColorContent v-if="finalId === 'stoneColor'" :color="value" mobile-mode>
                    <template #title><span></span></template>
                  </DiamondDetailColorContent>
                  <DiamondDetailCutContent v-if="finalId === 'stoneCut'" :cut="value" :stone-type="stoneType" mobile-mode>
                    <template #title><span></span></template>
                  </DiamondDetailCutContent>
                  <DiamondDetailFluorescenceContent v-if="finalId === 'stoneFluorescence'" :fluorescence="value"
                                                    mobile-mode>
                    <template #title><span></span></template>
                  </DiamondDetailFluorescenceContent>
                  <DiamondDetailCaratContent v-if="finalId === 'stoneSize'" :carat="value" mobile-mode>
                    <template #title><span></span></template>
                  </DiamondDetailCaratContent>
                  <InfoModalStoneType v-if="finalId === 'stoneType'" :value="value"/>
                  <InfoModalStonePresets v-if="finalId === 'stonePreset'" :value="stoneType"/>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </app-modal>
  </div>
</template>

<style scoped>
.font-serif-title {
  font-family: 'PalatinoLinotype', serif;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.modal-pop-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-pop-leave-active {
  transition: all 0.2s ease-in;
}

.modal-pop-enter-from {
  transform: scale(0.9) translateY(30px);
  opacity: 0;
}

.modal-pop-leave-to {
  transform: scale(0.95);
  opacity: 0;
}
</style>